import type {
  QuantFileTableData,
  QuantAnalysisSelection,
  QuantAnalysisSortConfig,
} from '@trinity/chat/src/api/interfaces/projects.quant-analysis';
import type { BarChart, MultipleBarChart, Bar } from '@trinity/chat/src/types/chat.chart';
import type { QuickChartData } from '@trinity/chat/src/types/widget-grid';
import { pathStartsWith } from '@trinity/chat/utils/quant-filter';
import { barToPie, pieToBar } from '../components/Chat/chart.utils';

/** Rounds numeric strings (e.g. "50.12" → "50", "60.1%" → "60%"). Non-numeric strings are returned as-is. */
export function roundCellValue(value: string): string {
  const unitInfo = extractUnit(value);
  const numericStr = unitInfo
    ? unitInfo.isPrefix
      ? value.slice(1)
      : value.slice(0, value.length - 1)
    : value;

  const num = parseFloat(numericStr);
  if (isNaN(num)) return value;

  const rounded = String(Math.round(num));
  return unitInfo
    ? unitInfo.isPrefix
      ? `${unitInfo.unit}${rounded}`
      : `${rounded}${unitInfo.unit}`
    : rounded;
}

export type ValueWithUnit = {
  value: number | string;
  unit?: string;
  isPrefix?: boolean;
};

type CreateBarChartParams = {
  categories: string[];
  values: number[];
  xAxisTitle: string;
  yAxisTitle: string;
  records: (string | number)[][];
  n?: number;
};

type CreateMultipleBarChartParams = {
  categories: string[];
  rowNames: string[][];
  records: (string | number)[][];
  xAxisTitle: string;
  seriesToN?: Record<string, number>;
};

/**
 * Rows/columns that contain these keywords should be excluded from charts
 * and ignored when sorting cross tabs. These typically represent aggregation rows
 * like totals, sums, averages, etc.
 */
export const AggregationKeywords = ['SUM', 'Net', 'Column n', 'Base n', 'Average'];

export function extractUnit(val: string | number): { unit: string; isPrefix: boolean } | null {
  if (typeof val !== 'string') return null;

  const trimmed = val.trim();

  // Check for suffix units (%, °, etc.)
  if (trimmed.endsWith('%')) {
    return { unit: '%', isPrefix: false };
  }
  if (trimmed.endsWith('°')) {
    return { unit: '°', isPrefix: false };
  }

  // Check for prefix units ($, €, £, ¥, etc.)
  const prefixMatches = trimmed.match(/^([$€£¥₹]+)/);
  if (prefixMatches) {
    return { unit: prefixMatches[1], isPrefix: true };
  }

  return null;
}

export function formatValueWithUnit({ value, unit, isPrefix }: ValueWithUnit): string {
  if (!unit) return String(value);
  return isPrefix ? `${unit}${value}` : `${value}${unit}`;
}

export function detectUnitFromRecords(
  records: (string | number)[][]
): { unit: string; isPrefix: boolean } | null {
  for (const row of records) {
    for (const val of row) {
      const detected = extractUnit(val);
      if (detected) {
        return detected;
      }
    }
  }
  return null;
}

export function resolveColumnName(
  columnIndex: number,
  originalNames: string[][],
  customNames?: Map<number, string[]>
): string[] {
  if (customNames?.has(columnIndex)) {
    return customNames.get(columnIndex);
  }
  return originalNames[columnIndex] || [];
}

export function resolveRowName(
  rowIndex: number,
  originalNames: string[][],
  customNames?: Map<number, string[]>
): string[] {
  if (customNames?.has(rowIndex)) {
    return customNames.get(rowIndex);
  }
  return originalNames[rowIndex] || [];
}

export function validateSelectionForChart(selection: QuantAnalysisSelection): boolean {
  if (!selection) {
    return true;
  }

  const isValidIndexSet = (values: Set<number>): boolean => {
    for (const value of values) {
      if (!Number.isFinite(value) || value < 0) {
        return false;
      }
    }
    return true;
  };

  if (!isValidIndexSet(selection.rows) || !isValidIndexSet(selection.columns)) {
    return false;
  }

  if (selection.cells.size === 0) {
    return true;
  }

  for (const cellKey of selection.cells.keys()) {
    const dashIndex = cellKey.indexOf('-');
    if (dashIndex === -1) {
      return false;
    }

    const rowIndex = Number.parseInt(cellKey.slice(0, dashIndex), 10);
    const colIndex = Number.parseInt(cellKey.slice(dashIndex + 1), 10);

    if (!Number.isFinite(rowIndex) || !Number.isFinite(colIndex)) {
      return false;
    }

    if (rowIndex < 0 || colIndex < 0) {
      return false;
    }
  }

  return true;
}

export function applyCustomNamesToFilteredData(
  filteredData: QuantFileTableData,
  originalData: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuantFileTableData {
  if (!customColumnNames && !customRowNames) {
    return filteredData;
  }

  // Create mapping from original indices to filtered indices
  const originalToFilteredColumnMap = new Map<number, number>();
  const originalToFilteredRowMap = new Map<number, number>();

  for (let origColIndex = 0; origColIndex < originalData.columnNames.length; origColIndex++) {
    const origCol = originalData.columnNames[origColIndex];
    const foundIndex = filteredData.columnNames.findIndex((filteredCol, idx) => {
      // Check if this filtered column corresponds to the original column
      return filteredCol.length === origCol.length && filteredCol.every((val, i) => val === origCol[i]);
    });
    if (foundIndex !== -1 && !originalToFilteredColumnMap.has(origColIndex)) {
      originalToFilteredColumnMap.set(origColIndex, foundIndex);
    }
  }

  for (let origRowIndex = 0; origRowIndex < originalData.rowNames.length; origRowIndex++) {
    const origRow = originalData.rowNames[origRowIndex];
    const foundIndex = filteredData.rowNames.findIndex((filteredRow, idx) => {
      // Check if this filtered row corresponds to the original row
      return filteredRow.length === origRow.length && filteredRow.every((val, i) => val === origRow[i]);
    });
    if (foundIndex !== -1 && !originalToFilteredRowMap.has(origRowIndex)) {
      originalToFilteredRowMap.set(origRowIndex, foundIndex);
    }
  }

  // Apply custom names using the mapping
  const result: QuantFileTableData = {
    ...filteredData,
    columnNames: filteredData.columnNames.map((col, filteredIndex) => {
      // Find original index for this filtered column
      for (const [origIndex, mappedFilteredIndex] of originalToFilteredColumnMap.entries()) {
        if (mappedFilteredIndex === filteredIndex && customColumnNames?.has(origIndex)) {
          return customColumnNames.get(origIndex);
        }
      }
      return col;
    }),
    rowNames: filteredData.rowNames.map((row, filteredIndex) => {
      // Find original index for this filtered row
      for (const [origIndex, mappedFilteredIndex] of originalToFilteredRowMap.entries()) {
        if (mappedFilteredIndex === filteredIndex && customRowNames?.has(origIndex)) {
          return customRowNames.get(origIndex);
        }
      }
      return row;
    }),
  };

  return result;
}

export function filterTableData(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][]
): QuantFileTableData {
  if (columnNamesFilter.length === 0 && rowNamesFilter.length === 0) {
    return data;
  }

  const filteredRowIndicesSet = new Set<number>();
  const filteredColumnIndicesSet = new Set<number>();

  if (rowNamesFilter.length > 0) {
    for (let i = 0; i < data.rowNames.length; i++) {
      const rowPath = data.rowNames[i];
      if (!rowPath) continue;

      for (const filterPath of rowNamesFilter) {
        if (pathStartsWith(rowPath, filterPath)) {
          filteredRowIndicesSet.add(i);
          break;
        }
      }
    }
  }

  if (columnNamesFilter.length > 0) {
    for (let i = 0; i < data.columnNames.length; i++) {
      const colPath = data.columnNames[i];
      if (!colPath || colPath.length === 0) continue;

      for (const filterPath of columnNamesFilter) {
        if (pathStartsWith(colPath, filterPath)) {
          filteredColumnIndicesSet.add(i);
          break;
        }
      }
    }
  }

  const filteredRowIndices = Array.from(filteredRowIndicesSet).sort((a, b) => a - b);
  const filteredColumnIndices = Array.from(filteredColumnIndicesSet).sort((a, b) => a - b);
  const filteredRowSet = filteredRowIndices.length > 0 ? new Set(filteredRowIndices) : null;
  const filteredColSet = filteredColumnIndices.length > 0 ? new Set(filteredColumnIndices) : null;

  const filteredRecords: string[][] = data.records
    .map((record, rowIndex) => {
      if (filteredRowSet && !filteredRowSet.has(rowIndex)) {
        return null;
      }
      if (!filteredColSet) {
        return record;
      }
      return record.filter((_, colIndex) => filteredColSet.has(colIndex));
    })
    .filter((record): record is string[] => record !== null);

  let filteredMetadata = data.metadata;
  if (data.metadata && (filteredRowSet || filteredColSet)) {
    filteredMetadata = {
      ...data.metadata,
      arrowColors: data.metadata.arrowColors
        ?.map((row, rowIndex) => {
          if (filteredRowSet && !filteredRowSet.has(rowIndex)) {
            return null;
          }
          if (!filteredColSet) {
            return row;
          }
          return row.filter((_, colIndex) => filteredColSet.has(colIndex));
        })
        .filter((row): row is (number | undefined)[] => row !== null),
      arrowDirection: data.metadata.arrowDirection
        ?.map((row, rowIndex) => {
          if (filteredRowSet && !filteredRowSet.has(rowIndex)) {
            return null;
          }
          if (!filteredColSet) {
            return row;
          }
          return row.filter((_, colIndex) => filteredColSet.has(colIndex));
        })
        .filter((row): row is (string | undefined)[] => row !== null),
      isSignificant: data.metadata.isSignificant
        ?.map((row, rowIndex) => {
          if (filteredRowSet && !filteredRowSet.has(rowIndex)) {
            return null;
          }
          if (!filteredColSet) {
            return row;
          }
          return row.filter((_, colIndex) => filteredColSet.has(colIndex));
        })
        .filter((row): row is (boolean | undefined)[] => row !== null),
    };
  }

  return {
    ...data,
    rowNames: filteredRowSet ? data.rowNames.filter((_, i) => filteredRowSet.has(i)) : data.rowNames,
    columnNames: filteredColSet ? data.columnNames.filter((_, i) => filteredColSet.has(i)) : data.columnNames,
    records: filteredRecords,
    metadata: filteredMetadata,
  };
}

export function parseNumericValue(val: string | number): number {
  try {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const parsed = parseFloat(val.replace(/[^0-9.-]+/g, ''));
      if (isNaN(parsed)) return 0;
      return parsed;
    }
    return 0;
  } catch {
    // If any error occurs during parsing, return 0
    return 0;
  }
}

type ManualSelectionChartParams = {
  chartType: QuickChartData['type'];
  tableData: QuantFileTableData;
  selectedCells: Map<string, boolean>;
  fallbackTitle: string;
  sortConfig?: QuantAnalysisSortConfig | null;
};

type ManualCellEntry = {
  rowIndex: number;
  colIndex: number;
  rowLabel: string;
  columnLabel: string;
  value: number;
};

export function createManualSelectionChart({
  chartType,
  tableData,
  selectedCells,
  fallbackTitle,
  sortConfig,
}: ManualSelectionChartParams): QuickChartData | null {
  if (!selectedCells || selectedCells.size === 0) {
    return null;
  }

  const valueMap = new Map<string, ManualCellEntry>();
  const rowLabelMap = new Map<number, string>();
  const columnLabelMap = new Map<number, string>();
  const rowIndexSet = new Set<number>();
  const columnIndexSet = new Set<number>();
  const recordsForUnits: (string | number)[][] = [];

  for (const cellKey of selectedCells.keys()) {
    const dashIndex = cellKey.indexOf('-');
    if (dashIndex === -1) {
      continue;
    }

    const rowIndex = Number.parseInt(cellKey.slice(0, dashIndex), 10);
    const colIndex = Number.parseInt(cellKey.slice(dashIndex + 1), 10);

    const rowPath = tableData.rowNames[rowIndex] || [];
    const columnPath = tableData.columnNames[colIndex] || [];

    const rawValue = tableData.records[rowIndex]?.[colIndex];
    const numericValue = parseNumericValue(rawValue ?? 0);

    const rowLabel = rowPath.length > 0 ? rowPath.join(' - ') : `Row ${rowIndex + 1}`;
    const columnLabel = columnPath.length > 0 ? columnPath.join(' - ') : `Column ${colIndex + 1}`;

    valueMap.set(`${rowIndex}-${colIndex}`, {
      rowIndex,
      colIndex,
      rowLabel,
      columnLabel,
      value: numericValue,
    });

    rowIndexSet.add(rowIndex);
    columnIndexSet.add(colIndex);

    if (!rowLabelMap.has(rowIndex)) {
      rowLabelMap.set(rowIndex, rowLabel);
    }
    if (!columnLabelMap.has(colIndex)) {
      columnLabelMap.set(colIndex, columnLabel);
    }

    recordsForUnits.push([rawValue ?? numericValue]);
  }

  if (valueMap.size === 0) {
    return null;
  }

  const unitInfo = detectUnitFromRecords(recordsForUnits);

  const orderedRowIndices = (() => {
    const indices = Array.from(rowIndexSet.values());
    if (!sortConfig) {
      return indices.sort((a, b) => a - b);
    }

    const { columnIndex, direction } = sortConfig;
    return indices.sort((a, b) => {
      const aValue = parseNumericValue(tableData.records[a]?.[columnIndex]);
      const bValue = parseNumericValue(tableData.records[b]?.[columnIndex]);
      if (aValue === bValue) {
        return a - b;
      }
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  })();

  const orderedColumnIndices = Array.from(columnIndexSet.values()).sort((a, b) => a - b);

  const orderedEntries: ManualCellEntry[] = [];

  for (const rowIndex of orderedRowIndices) {
    for (const colIndex of orderedColumnIndices) {
      const entry = valueMap.get(`${rowIndex}-${colIndex}`);
      if (entry) {
        orderedEntries.push(entry);
      }
    }
  }

  if (orderedEntries.length === 0) {
    return null;
  }

  const categories = orderedEntries.map((entry) => `${entry.rowLabel} • ${entry.columnLabel}`);
  const values = orderedEntries.map((entry) => entry.value);

  const baseChart: QuickChartData = {
    type: 'bar',
    chart: {
      title: fallbackTitle,
      xAxisTitle: 'Selection',
      yAxisTitle: unitInfo?.unit ?? 'Value',
      indexBy: 'x',
      data: categories.map((category, index) => ({
        x: category,
        y: values[index],
      })),
    },
    options: {
      unit: unitInfo?.unit,
      isPrefix: unitInfo?.isPrefix,
    },
  };

  if (chartType === 'bar') {
    return baseChart;
  }

  if (chartType === 'stacked-bar') {
    return {
      ...baseChart,
      type: 'stacked-bar',
    } as QuickChartData;
  }

  if (chartType === 'pie') {
    return transformChartType(baseChart, 'pie');
  }

  if (chartType === 'progress-bar-widget') {
    const maxProgressBars = 6;
    const limitedEntries = orderedEntries.slice(0, maxProgressBars);

    return {
      type: 'progress-bar-widget',
      title: fallbackTitle,
      backgroundColor: 'var(--gray-xl)',
      values: limitedEntries.map((entry) => String(entry.value)),
      labels: limitedEntries.map((entry) => `${entry.rowLabel} - ${entry.columnLabel}`),
      minValues: limitedEntries.map(() => '0'),
      maxValues: limitedEntries.map(() => '100'),
      units: limitedEntries.map(() => unitInfo?.unit ?? '%'),
    } as QuickChartData;
  }

  const seriesData: Bar[] = [];

  for (const colIndex of orderedColumnIndices) {
    const seriesLabel = columnLabelMap.get(colIndex);
    if (!seriesLabel) {
      continue;
    }

    const dataPoints: Bar['data'] = [];

    for (const rowIndex of orderedRowIndices) {
      const entry = valueMap.get(`${rowIndex}-${colIndex}`);
      if (!entry) {
        continue;
      }

      const label = rowLabelMap.get(rowIndex) ?? `Row ${rowIndex + 1}`;
      dataPoints.push({
        x: label,
        y: entry.value,
      });
    }

    if (dataPoints.length === 0) {
      continue;
    }

    seriesData.push({
      index: seriesLabel,
      data: dataPoints,
    });
  }

  if (seriesData.length === 0) {
    return chartType === 'multiple-stacked-bar' ? { ...baseChart, type: 'stacked-bar' } : baseChart;
  }

  return {
    type: chartType,
    chart: {
      title: fallbackTitle,
      xAxisTitle: orderedRowIndices.length > 1 ? 'Rows' : 'Selection',
      yAxisTitle: unitInfo?.unit ?? 'Values',
      indexBy: 'x',
      data: seriesData,
    },
    options: {
      unit: unitInfo?.unit,
      isPrefix: unitInfo?.isPrefix,
    },
  } as QuickChartData;
}

function extractColumnN(data: QuantFileTableData): number[] {
  for (let i = 0; i < data.rowNames.length; i++) {
    const row = data.rowNames[i];
    const hasColumnN = row.some((cell: string) => cell?.toLowerCase().includes('column n'));

    if (hasColumnN) {
      return data.records[i].map((val) => parseNumericValue(val));
    }
  }
  return [];
}

function createBarChart({
  categories,
  values,
  xAxisTitle,
  yAxisTitle,
  records,
  n,
}: CreateBarChartParams): QuickChartData {
  const chartData: { x: string; y: number }[] = categories.map((category, index) => ({
    x: category,
    y: values[index],
  }));

  const unitInfo = detectUnitFromRecords(records);

  return {
    type: 'bar' as const,
    chart: {
      title: 'Quantitative Analysis Results',
      xAxisTitle,
      yAxisTitle,
      indexBy: 'x',
      data: chartData,
    } as BarChart,
    options: {
      n,
      unit: unitInfo?.unit,
      isPrefix: unitInfo?.isPrefix,
    },
  };
}

function createMultipleClusteredBarChart({
  categories,
  rowNames,
  records,
  xAxisTitle,
  seriesToN,
}: CreateMultipleBarChartParams): QuickChartData {
  const unitInfo = detectUnitFromRecords(records);

  const seriesData = categories
    .map((category, colIndex) => {
      if (!category || (typeof category === 'string' && category.trim() === '')) {
        return null;
      }

      const dataPoints = rowNames.map((rowName, rowIndex) => ({
        x: rowName[0] || '',
        y: parseNumericValue(records[rowIndex]?.[colIndex]),
      }));

      if (dataPoints.length === 0) {
        return null;
      }

      return {
        index: category,
        data: dataPoints,
      } as Bar;
    })
    .filter((series): series is Bar =>
      Boolean(series && Array.isArray(series.data) && series.data.length > 0)
    );

  return {
    type: 'multiple-clustered-bar',
    chart: {
      title: 'Quantitative Analysis Results',
      xAxisTitle,
      yAxisTitle: 'Values',
      indexBy: 'x',
      data: seriesData,
    } as MultipleBarChart,
    options: {
      seriesToN,
      unit: unitInfo?.unit,
      isPrefix: unitInfo?.isPrefix,
    },
  };
}

function createMultipleStackedBarChart({
  categories,
  rowNames,
  records,
  xAxisTitle,
  seriesToN,
}: CreateMultipleBarChartParams): QuickChartData {
  const unitInfo = detectUnitFromRecords(records);

  const seriesData = categories
    .map((category, colIndex) => {
      if (!category || (typeof category === 'string' && category.trim() === '')) {
        return null;
      }

      const dataPoints = rowNames.map((rowName, rowIndex) => ({
        x: rowName[0] || '',
        y: parseNumericValue(records[rowIndex]?.[colIndex]),
      }));

      if (dataPoints.length === 0) {
        return null;
      }

      return {
        index: category,
        data: dataPoints,
      } as Bar;
    })
    .filter((series): series is Bar =>
      Boolean(series && Array.isArray(series.data) && series.data.length > 0)
    );

  return {
    type: 'multiple-stacked-bar',
    chart: {
      title: 'Quantitative Analysis Results',
      xAxisTitle,
      yAxisTitle: 'Values',
      indexBy: 'x',
      data: seriesData,
    } as MultipleBarChart,
    options: {
      seriesToN,
      unit: unitInfo?.unit,
      isPrefix: unitInfo?.isPrefix,
    },
  };
}

function createMultipleBarChart({
  categories,
  rowNames,
  records,
  xAxisTitle,
  seriesToN,
}: CreateMultipleBarChartParams): QuickChartData {
  // Use stacked as the default if we have many series, it scales better
  const seriesCount = categories.filter((cat) => cat).length;
  const threshold = 4;

  if (seriesCount >= threshold) {
    return createMultipleStackedBarChart({
      categories,
      rowNames,
      records,
      xAxisTitle,
      seriesToN,
    });
  } else {
    return createMultipleClusteredBarChart({
      categories,
      rowNames,
      records,
      xAxisTitle,
      seriesToN,
    });
  }
}

export function quickChartingRules(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null = null,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
) {
  // Apply custom names to original data before filtering
  let dataWithCustomNames = data;
  if (customColumnNames || customRowNames) {
    dataWithCustomNames = {
      ...data,
      columnNames: data.columnNames.map((_, index) =>
        resolveColumnName(index, data.columnNames, customColumnNames)
      ),
      rowNames: data.rowNames.map((_, index) => resolveRowName(index, data.rowNames, customRowNames)),
    };
  }

  let filteredData = filterTableData(dataWithCustomNames, columnNamesFilter, rowNamesFilter);

  // Extract column n BEFORE filtering out aggregation rows
  const columnNValues = extractColumnN(filteredData);

  // Filter out aggregation rows (SUM, Net, Base n, etc.) from chart data
  const nonAggregationRowIndices: number[] = [];
  for (let i = 0; i < filteredData.rowNames.length; i++) {
    const row = filteredData.rowNames[i];
    const isAggregationRow = AggregationKeywords.some((agg) =>
      row.some((cell: string) => cell?.toLowerCase().includes(agg.toLowerCase()))
    );
    if (!isAggregationRow) {
      nonAggregationRowIndices.push(i);
    }
  }

  // Filter out aggregation columns (SUM, Net, Base n, etc.) from chart data
  const nonAggregationColIndices: number[] = [];
  for (let i = 0; i < filteredData.columnNames.length; i++) {
    const col = filteredData.columnNames[i];
    const isAggregationCol = AggregationKeywords.some((agg) =>
      col.some((cell: string) => cell?.toLowerCase().includes(agg.toLowerCase()))
    );
    if (!isAggregationCol) {
      nonAggregationColIndices.push(i);
    }
  }

  // Apply aggregation row and column filters
  filteredData = {
    ...filteredData,
    rowNames: filteredData.rowNames.filter((_, i) => nonAggregationRowIndices.includes(i)),
    columnNames: filteredData.columnNames.filter((_, i) => nonAggregationColIndices.includes(i)),
    records: filteredData.records
      .filter((_, i) => nonAggregationRowIndices.includes(i))
      .map((row) => row.filter((_, i) => nonAggregationColIndices.includes(i))),
  };

  // Filter columnNValues to match filtered columns
  const filteredColumnNValues =
    columnNValues.length > 0 ? columnNValues.filter((_, i) => nonAggregationColIndices.includes(i)) : [];

  // Filter out empty arrays and arrays with only empty/null values
  filteredData = {
    ...filteredData,
    columnNames: filteredData.columnNames.filter((col) => col && col.length > 0 && col.some((cell) => cell)),
    rowNames: filteredData.rowNames.filter((row) => row && row.length > 0 && row.some((cell) => cell)),
  };

  // Early return if no valid data after filtering
  if (filteredData.columnNames.length === 0 || filteredData.rowNames.length === 0) {
    return null;
  }

  if (sortConfig) {
    const indexedData = filteredData.records.map((record, index) => ({
      record,
      rowName: filteredData.rowNames[index],
      originalIndex: index,
    }));

    // Sort by the selected column
    indexedData.sort((a, b) => {
      const aValue = a.record[sortConfig.columnIndex];
      const bValue = b.record[sortConfig.columnIndex];
      const aNum = parseNumericValue(aValue);
      const bNum = parseNumericValue(bValue);

      return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
    });

    filteredData = {
      ...filteredData,
      records: indexedData.map((item) => item.record),
      rowNames: indexedData.map((item) => item.rowName),
    };
  }

  const columnHierarchy = filteredData.columnNames[0]?.length || 0;
  const rowHierarchy = filteredData.rowNames[0]?.length || 0;

  // We don't support more than 1 level of row hierarchy or more than 3 levels of column hierarchy
  if (rowHierarchy > 1 || columnHierarchy > 3) {
    return null;
  }

  // Check if all data is 0 - don't show chart if so
  const allDataIsZero = filteredData.records.every((row) => row.every((val) => parseNumericValue(val) === 0));
  if (allDataIsZero) {
    return null;
  }

  // If we have 3 levels of nesting and multiple parents, we cannot chart this
  const truncatedColumnNValues = filteredColumnNValues;
  if (columnHierarchy === 3) {
    const columnParentCounts = countParentOccurrences(filteredData.columnNames);
    if (Object.keys(columnParentCounts).length > 1) {
      return null;
    }
  }

  // Single row, 2-level columns: simple bar chart
  if (rowHierarchy === 1 && columnHierarchy === 2 && filteredData.records.length === 1) {
    const categories = filteredData.columnNames.map((colPath) => `${colPath[0]} - ${colPath[1]}`);
    const values = filteredData.records[0].map((val) => parseNumericValue(val));
    return createBarChart({
      categories,
      values,
      xAxisTitle: filteredData.columnNames[0][0],
      yAxisTitle: rowNamesFilter[0]?.join(' > ') ?? '',
      records: filteredData.records,
      n: columnNValues[0],
    });
  }

  // 3-level columns: reduce to 2 levels and treat as multiple clustered bar chart
  if (rowHierarchy === 1 && columnHierarchy === 3) {
    const categories = filteredData.columnNames.map((colPath) => `${colPath[1]} - ${colPath[2]}`);

    const seriesMapping: Record<string, number> = {};
    if (truncatedColumnNValues.length > 0) {
      categories.forEach((category, index) => {
        if (index < truncatedColumnNValues.length && truncatedColumnNValues[index] > 0) {
          seriesMapping[category] = truncatedColumnNValues[index];
        }
      });
    }
    return createMultipleBarChart({
      categories,
      rowNames: filteredData.rowNames,
      records: filteredData.records,
      xAxisTitle: filteredData.columnNames[0][1],
      seriesToN: Object.keys(seriesMapping).length > 0 ? seriesMapping : undefined,
    });
  }

  // Multiple rows, 2-level columns: multiple clustered bar chart
  if (rowHierarchy === 1 && columnHierarchy === 2 && filteredData.records.length > 1) {
    const categories = filteredData.columnNames.map((colPath) => `${colPath[0]} - ${colPath[1]}`);

    const seriesMapping: Record<string, number> = {};
    if (truncatedColumnNValues.length > 0) {
      categories.forEach((category, index) => {
        if (index < truncatedColumnNValues.length && truncatedColumnNValues[index] > 0) {
          seriesMapping[category] = truncatedColumnNValues[index];
        }
      });
    }
    return createMultipleBarChart({
      categories,
      rowNames: filteredData.rowNames,
      records: filteredData.records,
      xAxisTitle: filteredData.columnNames[0][0],
      seriesToN: Object.keys(seriesMapping).length > 0 ? seriesMapping : undefined,
    });
  }

  // Multiple rows, 1-level columns: bar chart
  if (
    rowHierarchy === 1 &&
    columnHierarchy === 1 &&
    filteredData.columnNames.length === 1 &&
    filteredData.rowNames.length > 1
  ) {
    const categories = filteredData.rowNames.map((colPath) => colPath[0]);
    const values = filteredData.records.map((row) => parseNumericValue(row[0]));
    return createBarChart({
      categories,
      values,
      xAxisTitle: filteredData.columnNames[0][0],
      yAxisTitle: 'Values',
      records: filteredData.records,
      n: columnNValues[0],
    });
  }

  // Single row, single column: KPI card
  if (filteredData.columnNames.length === 1 && filteredData.rowNames.length === 1) {
    const rowLabel = filteredData.rowNames[0][0];
    const colLabel = filteredData.columnNames[0][0];
    const value = filteredData.records[0][0];
    const title = `${rowLabel} vs. ${colLabel}`;

    return {
      type: 'kpi-widget' as const,
      title,
      backgroundColor: 'var(--gray-xl)',
      values: [String(value || '')],
      labels: [title],
    };
  }

  return null;
}

function countParentOccurrences(data: string[][]): Record<string, number> {
  const parentCounts: Record<string, number> = {};
  data.forEach((path) => {
    const parent = path[0];
    parentCounts[parent] = (parentCounts[parent] || 0) + 1;
  });
  return parentCounts;
}

export function transformChartType(chart: QuickChartData, newType: string): QuickChartData {
  if (chart.type === 'bar' && newType === 'pie') {
    return {
      type: 'pie' as const,
      chart: barToPie(chart.chart),
    };
  }

  if (chart.type === 'pie' && newType === 'bar') {
    return {
      type: 'bar' as const,
      chart: pieToBar(chart.chart),
    };
  }

  return {
    ...chart,
    type: newType,
  } as QuickChartData;
}

export function convertColorIntToHex(colorInt: number): string {
  const unsignedColor = (colorInt >>> 0) & 0xffffff;
  return '#' + unsignedColor.toString(16).padStart(6, '0').toUpperCase();
}

export function convertSelectionToFilters(
  selection: QuantAnalysisSelection,
  tableData: QuantFileTableData | null,
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null = null
): {
  columnNames: string[];
  rowNames: string[];
  selectedCells?: Map<string, boolean>;
  columnPaths: string[][];
  rowPaths: string[][];
} {
  if (!tableData) {
    return {
      columnNames: [],
      rowNames: [],
      columnPaths: [],
      rowPaths: [],
    };
  }

  if (selection.cells.size > 0 && selection.rows.size === 0 && selection.columns.size === 0) {
    const rowPathSet = new Map<string, string[]>();
    const columnPathSet = new Map<string, string[]>();

    for (const cellKey of selection.cells.keys()) {
      const dashIndex = cellKey.indexOf('-');
      if (dashIndex === -1) {
        continue;
      }

      const rowIndex = Number.parseInt(cellKey.slice(0, dashIndex), 10);
      const colIndex = Number.parseInt(cellKey.slice(dashIndex + 1), 10);

      const rowPath = tableData.rowNames[rowIndex];
      if (rowPath) {
        const rowKey = rowPath.join('||');
        if (!rowPathSet.has(rowKey)) {
          rowPathSet.set(rowKey, rowPath);
        }
      }

      const columnPath = tableData.columnNames[colIndex];
      if (columnPath) {
        const colKey = columnPath.join('||');
        if (!columnPathSet.has(colKey)) {
          columnPathSet.set(colKey, columnPath);
        }
      }
    }

    return {
      columnNames: [],
      rowNames: [],
      selectedCells: new Map(selection.cells),
      columnPaths: Array.from(columnPathSet.values()),
      rowPaths: Array.from(rowPathSet.values()),
    };
  }

  const columnNames: string[] = [];
  const rowNames: string[] = [];
  const columnNamesSet = new Set<string>();
  const rowNamesSet = new Set<string>();
  const columnPathSet = new Map<string, string[]>();
  const rowPathSet = new Map<string, string[]>();

  let sortedRowNames = tableData.rowNames;

  if (sortConfig && selection.rows.size > 0) {
    const rowCount = tableData.rowNames.length;
    const nonAggregationIndices: number[] = [];
    const aggregationIndices: number[] = [];

    const aggKeywordsLower = AggregationKeywords.map((k) => k.toLowerCase());
    const aggKeywordsCount = aggKeywordsLower.length;

    for (let i = 0; i < rowCount; i++) {
      const row = tableData.rowNames[i];
      if (!row) continue;

      let isAggregationRow = false;
      const rowLen = row.length;
      for (let j = 0; j < rowLen; j++) {
        const cell = (row[j] || '').toLowerCase();
        for (let k = 0; k < aggKeywordsCount; k++) {
          if (cell.includes(aggKeywordsLower[k])) {
            isAggregationRow = true;
            break;
          }
        }
        if (isAggregationRow) break;
      }

      if (isAggregationRow) {
        aggregationIndices.push(i);
      } else {
        nonAggregationIndices.push(i);
      }
    }

    const nonAggregationSet = new Set(nonAggregationIndices);
    const nonAggregationData: Array<{
      record: (string | number)[];
      rowName: string[];
      originalIndex: number;
    }> = [];

    for (let i = 0; i < rowCount; i++) {
      if (nonAggregationSet.has(i)) {
        nonAggregationData.push({
          record: tableData.records[i],
          rowName: tableData.rowNames[i],
          originalIndex: i,
        });
      }
    }

    const aggregationSet = new Set(aggregationIndices);
    const aggregationData: Array<{ record: (string | number)[]; rowName: string[]; originalIndex: number }> =
      [];

    for (let i = 0; i < rowCount; i++) {
      if (aggregationSet.has(i)) {
        aggregationData.push({
          record: tableData.records[i],
          rowName: tableData.rowNames[i],
          originalIndex: i,
        });
      }
    }

    const sortCol = sortConfig.columnIndex;
    const sortDir = sortConfig.direction === 'asc' ? 1 : -1;

    nonAggregationData.sort((a, b) => {
      const aNum = parseNumericValue(a.record[sortCol]);
      const bNum = parseNumericValue(b.record[sortCol]);
      return (aNum - bNum) * sortDir;
    });

    const totalLen = nonAggregationData.length + aggregationData.length;
    sortedRowNames = new Array(totalLen) as string[][];
    for (let i = 0; i < nonAggregationData.length; i++) {
      sortedRowNames[i] = nonAggregationData[i].rowName;
    }
    const aggOffset = nonAggregationData.length;
    for (let i = 0; i < aggregationData.length; i++) {
      sortedRowNames[aggOffset + i] = aggregationData[i].rowName;
    }
  }

  if (selection.rows.size > 0) {
    const rowCount = sortedRowNames.length;
    for (const rowIndex of selection.rows) {
      if (rowIndex >= 0 && rowIndex < rowCount) {
        const rowName = sortedRowNames[rowIndex];
        if (!rowName) {
          continue;
        }

        const rowKey = rowName.join('||');
        if (!rowPathSet.has(rowKey)) {
          rowPathSet.set(rowKey, rowName);
        }

        const firstLevel = rowName[0];
        if (firstLevel && !rowNamesSet.has(firstLevel)) {
          rowNamesSet.add(firstLevel);
          rowNames.push(firstLevel);
        }
      }
    }
  }

  if (selection.columns.size > 0) {
    const colCount = tableData.columnNames.length;
    for (const colIndex of selection.columns) {
      if (colIndex >= 0 && colIndex < colCount) {
        const colName = tableData.columnNames[colIndex];
        if (colName && colName.length > 0) {
          const columnKey = colName.join('||');
          if (!columnPathSet.has(columnKey)) {
            columnPathSet.set(columnKey, colName);
          }

          let filterName: string;
          if (colName.length === 1) {
            filterName = colName[0];
          } else {
            filterName = colName.join(' - ');
            if (columnNamesSet.has(filterName)) {
              filterName = colName[colName.length - 1];
            }
          }

          if (!columnNamesSet.has(filterName)) {
            columnNamesSet.add(filterName);
            columnNames.push(filterName);
          }
        }
      }
    }
  }

  if (selection.cells.size > 0) {
    for (const cellKey of selection.cells.keys()) {
      const dashIndex = cellKey.indexOf('-');
      if (dashIndex === -1) {
        continue;
      }

      const rowIndex = Number.parseInt(cellKey.slice(0, dashIndex), 10);
      const colIndex = Number.parseInt(cellKey.slice(dashIndex + 1), 10);

      const rowPath = tableData.rowNames[rowIndex];
      if (rowPath) {
        const key = rowPath.join('||');
        if (!rowPathSet.has(key)) {
          rowPathSet.set(key, rowPath);
        }
      }

      const columnPath = tableData.columnNames[colIndex];
      if (columnPath) {
        const key = columnPath.join('||');
        if (!columnPathSet.has(key)) {
          columnPathSet.set(key, columnPath);
        }
      }
    }
  }

  return {
    columnNames,
    rowNames,
    selectedCells: selection.cells.size > 0 ? new Map(selection.cells) : undefined,
    columnPaths: Array.from(columnPathSet.values()),
    rowPaths: Array.from(rowPathSet.values()),
  };
}

function tryGenerateMultipleClusteredBar(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  const filteredData = filterTableData(data, columnNamesFilter, rowNamesFilter);

  if (!filteredData || filteredData.records.length === 0 || filteredData.columnNames.length === 0) {
    return null;
  }

  if (filteredData.records.length === 1 && filteredData.columnNames.length > 1) {
    const colCount = filteredData.columnNames.length;
    const categories = new Array(colCount) as string[];

    for (let i = 0; i < colCount; i++) {
      const colPath = filteredData.columnNames[i];
      if (colPath.length === 1) {
        categories[i] = colPath[0];
      } else if (colPath.length === 2) {
        categories[i] = `${colPath[0]} - ${colPath[1]}`;
      } else if (colPath.length === 3) {
        categories[i] = `${colPath[1]} - ${colPath[2]}`;
      } else {
        categories[i] = colPath.join(' - ');
      }
    }

    const xAxisTitle = filteredData.columnNames[0]?.[0] || 'Categories';

    const columnNValues = extractColumnN(filteredData);
    let seriesMapping: Record<string, number> | undefined;
    if (columnNValues.length > 0) {
      seriesMapping = {};
      for (let i = 0; i < categories.length && i < columnNValues.length; i++) {
        if (columnNValues[i] > 0) {
          seriesMapping[categories[i]] = columnNValues[i];
        }
      }
      if (Object.keys(seriesMapping).length === 0) {
        seriesMapping = undefined;
      }
    }

    return createMultipleBarChart({
      categories,
      rowNames: filteredData.rowNames,
      records: filteredData.records,
      xAxisTitle,
      seriesToN: seriesMapping,
    });
  }

  const result = quickChartingRules(
    data,
    columnNamesFilter,
    rowNamesFilter,
    sortConfig,
    customColumnNames,
    customRowNames
  );

  if (result && result.type === 'multiple-clustered-bar') {
    return result;
  }

  return null;
}

function tryGenerateMultipleStackedBar(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  const result = quickChartingRules(
    data,
    columnNamesFilter,
    rowNamesFilter,
    sortConfig,
    customColumnNames,
    customRowNames
  );

  if (result && result.type === 'multiple-clustered-bar') {
    return {
      ...result,
      type: 'multiple-stacked-bar',
    };
  }

  return null;
}

/**
 * Generates a KPI widget from table data.
 * Can be used with selected cells or will iterate over all data as fallback.
 */
export function tryGenerateKpiWidget(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  chartTitle: string,
  defaultColor: string,
  selectedCells?: Map<string, boolean>,
  filteredTableData?: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  // Apply custom names to original data before filtering if not using pre-filtered data
  let dataToUse = data;
  if (!filteredTableData && (customColumnNames || customRowNames)) {
    dataToUse = {
      ...data,
      columnNames: data.columnNames.map((_, index) =>
        resolveColumnName(index, data.columnNames, customColumnNames)
      ),
      rowNames: data.rowNames.map((_, index) => resolveRowName(index, data.rowNames, customRowNames)),
    };
  }

  const filteredData = filteredTableData || filterTableData(dataToUse, columnNamesFilter, rowNamesFilter);

  if (!filteredData?.records || filteredData.records.length === 0) {
    return null;
  }

  const values: string[] = [];
  const labels: string[] = [];
  const maxValues = 6;

  if (selectedCells && selectedCells.size > 0) {
    for (const cellKey of selectedCells.keys()) {
      if (values.length >= maxValues) break;

      const dashIndex = cellKey.indexOf('-');
      if (dashIndex === -1) continue;

      const rowIndex = parseInt(cellKey.substring(0, dashIndex), 10);
      const colIndex = parseInt(cellKey.substring(dashIndex + 1), 10);

      if (isNaN(rowIndex) || isNaN(colIndex)) continue;
      if (rowIndex < 0 || rowIndex >= filteredData.records.length) continue;

      const row = filteredData.records[rowIndex];
      if (!row || colIndex < 0 || colIndex >= row.length) continue;

      values.push(String(row[colIndex] || ''));

      const rowName = filteredData.rowNames[rowIndex];
      const rowLabel = Array.isArray(rowName)
        ? rowName.join(' - ')
        : String(rowName || `Row ${rowIndex + 1}`);

      const colName = filteredData.columnNames[colIndex];
      const colLabel = Array.isArray(colName)
        ? colName.join(' - ')
        : String(colName || `Col ${colIndex + 1}`);

      labels.push(`${rowLabel} - ${colLabel}`);
    }
  } else {
    const rowCount = filteredData.records.length;
    const colCount = filteredData.columnNames.length;

    for (let rowIndex = 0; rowIndex < rowCount && values.length < maxValues; rowIndex++) {
      const row = filteredData.records[rowIndex];
      if (!row) continue;

      const rowLen = row.length;
      for (let colIndex = 0; colIndex < rowLen && values.length < maxValues; colIndex++) {
        values.push(String(row[colIndex] || ''));

        const rowName = filteredData.rowNames[rowIndex];
        const rowLabel = Array.isArray(rowName)
          ? rowName.join(' - ')
          : String(rowName || `Row ${rowIndex + 1}`);

        const colName = colIndex < colCount ? filteredData.columnNames[colIndex] : undefined;
        const colLabel = Array.isArray(colName)
          ? colName.join(' - ')
          : String(colName || `Col ${colIndex + 1}`);

        labels.push(`${rowLabel} - ${colLabel}`);
      }
    }
  }

  if (values.length === 0) {
    return null;
  }

  return {
    type: 'kpi-widget',
    title: chartTitle,
    backgroundColor: defaultColor,
    values,
    labels,
  };
}

/**
 * Generates a Progress Bar widget from table data.
 * Can be used with selected cells or will iterate over all data as fallback.
 */
export function tryGenerateProgressBarWidget(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  chartTitle: string,
  defaultColor: string,
  selectedCells?: Map<string, boolean>,
  filteredTableData?: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  // Apply custom names to original data before filtering if not using pre-filtered data
  let dataToUse = data;
  if (!filteredTableData && (customColumnNames || customRowNames)) {
    dataToUse = {
      ...data,
      columnNames: data.columnNames.map((_, index) =>
        resolveColumnName(index, data.columnNames, customColumnNames)
      ),
      rowNames: data.rowNames.map((_, index) => resolveRowName(index, data.rowNames, customRowNames)),
    };
  }

  const filteredData = filteredTableData || filterTableData(dataToUse, columnNamesFilter, rowNamesFilter);

  if (!filteredData?.records || filteredData.records.length === 0) {
    return null;
  }

  const values: string[] = [];
  const labels: string[] = [];
  const minValues: string[] = [];
  const maxValues: string[] = [];
  const units: string[] = [];
  const maxProgressBars = 6;

  if (selectedCells && selectedCells.size > 0) {
    for (const cellKey of selectedCells.keys()) {
      if (values.length >= maxProgressBars) break;

      const dashIndex = cellKey.indexOf('-');
      if (dashIndex === -1) continue;

      const rowIndex = parseInt(cellKey.substring(0, dashIndex), 10);
      const colIndex = parseInt(cellKey.substring(dashIndex + 1), 10);

      if (isNaN(rowIndex) || isNaN(colIndex)) continue;
      if (rowIndex < 0 || rowIndex >= filteredData.records.length) continue;

      const row = filteredData.records[rowIndex];
      if (!row || colIndex < 0 || colIndex >= row.length) continue;

      const numericValue = parseNumericValue(row[colIndex] ?? 0);
      values.push(String(numericValue));
      minValues.push('0');
      maxValues.push('100');
      units.push('%');

      const rowName = filteredData.rowNames[rowIndex];
      const rowLabel = Array.isArray(rowName)
        ? rowName.join(' - ')
        : String(rowName || `Row ${rowIndex + 1}`);

      const colName = filteredData.columnNames[colIndex];
      const colLabel = Array.isArray(colName)
        ? colName.join(' - ')
        : String(colName || `Col ${colIndex + 1}`);

      labels.push(`${rowLabel} - ${colLabel}`);
    }
  } else {
    const rowCount = filteredData.records.length;
    const colCount = filteredData.columnNames.length;

    for (let rowIndex = 0; rowIndex < rowCount && values.length < maxProgressBars; rowIndex++) {
      const row = filteredData.records[rowIndex];
      if (!row) continue;

      const rowLen = row.length;
      for (let colIndex = 0; colIndex < rowLen && values.length < maxProgressBars; colIndex++) {
        const numericValue = parseNumericValue(row[colIndex] ?? 0);
        values.push(String(numericValue));
        minValues.push('0');
        maxValues.push('100');
        units.push('%');

        const rowName = filteredData.rowNames[rowIndex];
        const rowLabel = Array.isArray(rowName)
          ? rowName.join(' - ')
          : String(rowName || `Row ${rowIndex + 1}`);

        const colName = colIndex < colCount ? filteredData.columnNames[colIndex] : undefined;
        const colLabel = Array.isArray(colName)
          ? colName.join(' - ')
          : String(colName || `Col ${colIndex + 1}`);

        labels.push(`${rowLabel} - ${colLabel}`);
      }
    }
  }

  if (values.length === 0) {
    return null;
  }

  return {
    type: 'progress-bar-widget',
    title: chartTitle,
    backgroundColor: defaultColor,
    values,
    labels,
    minValues,
    maxValues,
    units,
  };
}

function tryGenerateDataTable(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null,
  chartTitle: string,
  defaultColor: string,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  // Apply custom names to original data before filtering
  let dataWithCustomNames = data;
  if (customColumnNames || customRowNames) {
    dataWithCustomNames = {
      ...data,
      columnNames: data.columnNames.map((_, index) =>
        resolveColumnName(index, data.columnNames, customColumnNames)
      ),
      rowNames: data.rowNames.map((_, index) => resolveRowName(index, data.rowNames, customRowNames)),
    };
  }

  const filteredData = filterTableData(dataWithCustomNames, columnNamesFilter, rowNamesFilter);

  if (!filteredData || filteredData.columnNames.length === 0 || filteredData.rowNames.length === 0) {
    return null;
  }

  return {
    type: 'data-table',
    title: chartTitle || 'Data Table',
    headerColor: defaultColor,
    data: structuredClone(filteredData),
    sortConfig,
  };
}

export function tryGenerateChartType(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null,
  chartType:
    | 'multiple-clustered-bar'
    | 'multiple-stacked-bar'
    | 'kpi-widget'
    | 'data-table'
    | 'progress-bar-widget',
  chartTitle = 'Quantitative Analysis',
  defaultColor = 'var(--gray-xl)',
  selectedCells?: Map<string, boolean>,
  filteredTableData?: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  switch (chartType) {
    case 'multiple-clustered-bar':
      return tryGenerateMultipleClusteredBar(
        data,
        columnNamesFilter,
        rowNamesFilter,
        sortConfig,
        customColumnNames,
        customRowNames
      );
    case 'multiple-stacked-bar':
      return tryGenerateMultipleStackedBar(
        data,
        columnNamesFilter,
        rowNamesFilter,
        sortConfig,
        customColumnNames,
        customRowNames
      );
    case 'kpi-widget':
      return tryGenerateKpiWidget(
        data,
        columnNamesFilter,
        rowNamesFilter,
        chartTitle,
        defaultColor,
        selectedCells,
        filteredTableData,
        customColumnNames,
        customRowNames
      );
    case 'progress-bar-widget':
      return tryGenerateProgressBarWidget(
        data,
        columnNamesFilter,
        rowNamesFilter,
        chartTitle,
        defaultColor,
        selectedCells,
        filteredTableData,
        customColumnNames,
        customRowNames
      );
    case 'data-table':
      return tryGenerateDataTable(
        data,
        columnNamesFilter,
        rowNamesFilter,
        sortConfig,
        chartTitle,
        defaultColor,
        customColumnNames,
        customRowNames
      );
    default:
      return null;
  }
}

export function generateChartWithPriority(
  data: QuantFileTableData,
  columnNamesFilter: string[][],
  rowNamesFilter: string[][],
  sortConfig: { columnIndex: number; direction: 'asc' | 'desc' } | null,
  chartTitle = 'Quantitative Analysis',
  defaultColor = 'var(--gray-xl)',
  selectedCells?: Map<string, boolean>,
  filteredTableData?: QuantFileTableData,
  customColumnNames?: Map<number, string[]>,
  customRowNames?: Map<number, string[]>
): QuickChartData | null {
  // If we have a small, explicit cell selection (up to 6 cells),
  // always prefer a KPI widget (metric card).
  if (selectedCells && selectedCells.size > 0 && selectedCells.size <= 6) {
    const kpi = tryGenerateKpiWidget(
      data,
      columnNamesFilter,
      rowNamesFilter,
      chartTitle,
      defaultColor,
      selectedCells,
      filteredTableData,
      customColumnNames,
      customRowNames
    );
    if (kpi) {
      return kpi;
    }
  }

  const chartTypes: Array<'multiple-clustered-bar' | 'multiple-stacked-bar' | 'kpi-widget' | 'data-table'> = [
    'multiple-clustered-bar',
    'multiple-stacked-bar',
    'kpi-widget',
    'data-table',
  ];

  for (const chartType of chartTypes) {
    const result = tryGenerateChartType(
      data,
      columnNamesFilter,
      rowNamesFilter,
      sortConfig,
      chartType,
      chartTitle,
      defaultColor,
      selectedCells,
      filteredTableData,
      customColumnNames,
      customRowNames
    );
    if (result) {
      return result;
    }
  }

  return null;
}
